import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { DeleteConfirmDialog } from '../ui/delete-confirm-dialog'
import {
  useAdminBillingIncluded,
  useCreateAdminBillingIncluded,
  useDeleteAdminBillingIncluded,
} from '../../hooks/use-admin-billing'

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7)
}

/**
 * BillingIncludedSection lists and adds the prepaid quantities of one
 * organization (spec 2026-10-08-01). A row is never edited: a change is a new
 * row with a later month.
 */
export function BillingIncludedSection({ orgUid }: { orgUid: string }) {
  const { t, i18n } = useTranslation('admin')
  const lang = i18n.language.startsWith('fr') ? 'fr' : 'en'
  const { data, isLoading } = useAdminBillingIncluded(orgUid)
  const create = useCreateAdminBillingIncluded()
  const remove = useDeleteAdminBillingIncluded()
  const codes = data?.codes ?? []
  const [code, setCode] = useState('staff_total')
  const [quantity, setQuantity] = useState('')
  const [month, setMonth] = useState(currentMonth())
  const [formError, setFormError] = useState<string | null>(null)
  const nf = new Intl.NumberFormat(i18n.language)

  const label = (c: string) => {
    const found = codes.find((x) => x.code === c)
    return found ? (found.label[lang] ?? found.label.en) : c
  }

  const submit = () => {
    if (!/^\d+$/.test(quantity.trim()) || !month) {
      setFormError(t('billing.included.invalid'))
      return
    }
    setFormError(null)
    create.mutate(
      { params: { path: { orgUid } }, body: { code, quantity: Number(quantity), effective_from: `${month}-01` } },
      {
        onSuccess: () => setQuantity(''),
        onError: () => setFormError(t('billing.included.save_error')),
      },
    )
  }

  return (
    <section className="space-y-4" data-testid="billing-included">
      <div>
        <h2 className="text-lg font-semibold">{t('billing.included.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('billing.included.help')}</p>
      </div>

      <div className="bg-card rounded-lg border border-border overflow-hidden">
        {isLoading ? (
          <div className="px-6 py-4 text-center text-muted-foreground">{t('billing.loading')}</div>
        ) : (data?.data ?? []).length === 0 ? (
          <div className="px-6 py-4 text-center text-muted-foreground" data-testid="billing-included-empty">
            {t('billing.included.empty')}
          </div>
        ) : (
          <Table data-testid="billing-included-rows">
            <TableHeader>
              <TableRow>
                <TableHead>{t('billing.included.column.code')}</TableHead>
                <TableHead className="text-right">{t('billing.included.column.quantity')}</TableHead>
                <TableHead>{t('billing.included.column.from')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.data ?? []).map((r) => {
                const key = `${r.code}-${r.effective_from.slice(0, 7)}`
                return (
                  <TableRow key={r.uid} data-testid={`billing-included-row-${key}`}>
                    <TableCell>{label(r.code)}</TableCell>
                    <TableCell className="text-right tabular-nums">{nf.format(r.quantity)}</TableCell>
                    <TableCell>{r.effective_from.slice(0, 7)}</TableCell>
                    <TableCell className="text-right">
                      <DeleteConfirmDialog
                        title={t('billing.included.delete_title')}
                        description={t('billing.included.delete_description')}
                        onConfirm={() => remove.mutate({ params: { path: { orgUid, uid: r.uid } } })}
                        isDeleting={remove.isPending}
                        trigger={
                          <Button variant="ghost" size="sm" data-testid={`billing-included-delete-${key}`}>
                            {t('billing.included.delete')}
                          </Button>
                        }
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="bg-card rounded-lg border border-border p-4 flex flex-wrap items-end gap-3" data-testid="billing-included-form">
        <div className="space-y-1">
          <Label>{t('billing.included.column.code')}</Label>
          <Select value={code} onValueChange={setCode}>
            <SelectTrigger className="w-60" data-testid="billing-included-code">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {codes.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.label[lang] ?? c.label.en}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>{t('billing.included.quantity')}</Label>
          <Input
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            inputMode="numeric"
            placeholder="5000"
            className="w-36"
            data-testid="billing-included-quantity"
          />
        </div>
        <div className="space-y-1">
          <Label>{t('billing.included.column.from')}</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="billing-included-month" />
        </div>
        <Button onClick={submit} disabled={create.isPending} data-testid="billing-included-add">
          {t('billing.included.add')}
        </Button>
        {formError && (
          <div className="w-full text-sm text-destructive" data-testid="billing-included-error">
            {formError}
          </div>
        )}
      </div>
    </section>
  )
}
